/**
 * Toast.tsx — Animated toast notification system.
 *
 * Features:
 * - Stackable (max 3 visible)
 * - Types: success, error, info, warning
 * - Animated progress bar showing remaining time
 * - Slide-in/out animations
 * - Swipe-to-dismiss on mobile
 */
import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
  createdAt: number;
}

const ICONS: Record<ToastType, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};

const STYLES: Record<ToastType, { border: string; icon: string; progress: string }> = {
  success: {
    border: 'border-green-400/50',
    icon: 'text-green-500',
    progress: 'bg-green-500',
  },
  error: {
    border: 'border-red-400/50',
    icon: 'text-red-500',
    progress: 'bg-red-500',
  },
  info: {
    border: 'border-blue-400/50',
    icon: 'text-blue-500',
    progress: 'bg-blue-500',
  },
  warning: {
    border: 'border-amber-400/50',
    icon: 'text-amber-500',
    progress: 'bg-amber-500',
  },
};

const _EMOJIS: Record<ToastType, string> = {
  success: '✅',
  error: '❌',
  info: 'ℹ️',
  warning: '⚠️',
};

// ── Single Toast ──────────────────────────────────────

const SingleToast: React.FC<{
  toast: ToastItem;
  onDismiss: (id: string) => void;
  index: number;
}> = ({ toast, onDismiss, index }) => {
  const [exiting, setExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const elRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);

  const style = STYLES[toast.type];
  const Icon = ICONS[toast.type];

  // Progress countdown
  useEffect(() => {
    const start = toast.createdAt;
    const end = start + toast.duration;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(0, ((end - now) / toast.duration) * 100);
      setProgress(remaining);
      if (remaining > 0) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        handleDismiss();
      }
    };
    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [toast]);

  const handleDismiss = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 250);
  };

  // Swipe-to-dismiss
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
    if (elRef.current) {
      elRef.current.style.transform = `translateX(${touchDeltaX.current}px)`;
      elRef.current.style.opacity = `${Math.max(0.3, 1 - Math.abs(touchDeltaX.current) / 200)}`;
    }
  };
  const handleTouchEnd = () => {
    if (Math.abs(touchDeltaX.current) > 80) {
      handleDismiss();
    } else if (elRef.current) {
      elRef.current.style.transform = '';
      elRef.current.style.opacity = '';
    }
    touchDeltaX.current = 0;
  };

  return (
    <div
      ref={elRef}
      role="alert"
      className={`
        glass-strong relative overflow-hidden ${style.border}
        transition-all duration-250 ease-out
        ${exiting ? 'opacity-0 -translate-y-3 scale-95' : 'opacity-100 translate-y-0 scale-100'}
      `}
      style={{
        animationDelay: `${index * 50}ms`,
        animation: exiting ? 'none' : 'toast-enter 0.3s ease-out backwards',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <Icon size={18} className={`${style.icon} flex-shrink-0`} />
        <span className="flex-1 text-sm font-medium text-[var(--f-text)] leading-snug">
          {toast.message}
        </span>
        <button
          onClick={handleDismiss}
          className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors flex-shrink-0"
        >
          <X size={14} className="text-[var(--f-text-muted)]" />
        </button>
      </div>
      {/* Progress bar */}
      <div className="h-[2px] w-full bg-black/5 dark:bg-white/5">
        <div
          className={`h-full ${style.progress} transition-none`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// ── Toast Container ───────────────────────────────────

export const ToastContainer: React.FC<{
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[min(90vw,380px)] space-y-2 pointer-events-none">
      {toasts.slice(0, 3).map((toast, i) => (
        <div key={toast.id} className="pointer-events-auto">
          <SingleToast toast={toast} onDismiss={onDismiss} index={i} />
        </div>
      ))}
    </div>
  );
};

export default ToastContainer;
